import {
    Column,
    ColumnDef,
    ColumnFiltersState,
    createColumnHelper,
    flexRender,
    getCoreRowModel,
    getFacetedMinMaxValues,
    getFacetedRowModel,
    getFacetedUniqueValues,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    PaginationState,
    RowSelectionState,
    SortingState,
    useReactTable,
} from "@tanstack/react-table";
import {
    CSSProperties,
    HTMLProps,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa";
import { DebouncedInput } from "./input";
import { TiArrowSortedUp } from "react-icons/ti";
import clsx from "clsx";

export type TableProps<T> = {
    data: T[];
    columns: ColumnDef<T, any>[];
    enableSorting?: boolean;
    enablePagination?: boolean;
    pageSize?: number;
    containerClassName?: string;
    orderNotesLoading?: boolean | null;
    columnOrder?: string[];
    enableRowSelection?: boolean;
    onRowSelection?: (row: RowSelectionState) => void;
    tableSize?: "xs" | "sm" | "md" | "lg";
    title: string;
};

export function Table<T>({
    data,
    columns,
    enableSorting = true,
    enablePagination = true,
    pageSize = 10,
    orderNotesLoading,
    columnOrder,
    enableRowSelection = false,
    onRowSelection,
    tableSize,
    title,
}: TableProps<T>) {
    const columnHelper = createColumnHelper<T>();
    const [sorting, setSorting] = useState<SortingState>([]);
    const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
    const [pagination, setPagination] = useState<PaginationState>({
        pageIndex: 0,
        pageSize,
    });
    const [rowSelection, setRowSelection] = useState({});
    const prevDataRef = useRef(data);

    const table = useReactTable({
        data,
        columns: enableRowSelection
            ? [
                  columnHelper.display({
                      id: "select",
                      cell: ({ row }) => (
                          <div className="flex justify-center">
                              <IndeterminateCheckbox
                                  {...{
                                      checked: row.getIsSelected(),
                                      disabled: !row.getCanSelect(),
                                      indeterminate: row.getIsSomeSelected(),
                                      onChange: row.getToggleSelectedHandler(),
                                  }}
                              />
                          </div>
                      ),
                      size: 30,
                  }),
                  ...columns,
              ]
            : columns,
        state: {
            sorting,
            columnOrder:
                enableRowSelection &&
                columnOrder !== undefined &&
                columnOrder.length !== 0
                    ? ["select", ...columnOrder]
                    : columnOrder,
            columnFilters,
            pagination,
            rowSelection,
        },
        enableRowSelection: true,
        onRowSelectionChange: setRowSelection,
        onColumnFiltersChange: setColumnFilters,
        onSortingChange: setSorting,
        onPaginationChange: setPagination,
        getPaginationRowModel: getPaginationRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getCoreRowModel: getCoreRowModel(),
        getFacetedRowModel: getFacetedRowModel(),
        getFacetedUniqueValues: getFacetedUniqueValues(),
        getFacetedMinMaxValues: getFacetedMinMaxValues(),
        enableMultiRowSelection: false,
    });

    const getCommonPinningStyles = (column: Column<T>): CSSProperties => {
        const isPinned = column.getIsPinned();
        /*const isLastLeftPinnedColumn =
            isPinned === "left" && column.getIsLastColumn("left");
        const isFirstRightPinnedColumn =
            isPinned === "right" && column.getIsFirstColumn("right");*/

        return {
            //boxShadow: isLastLeftPinnedColumn
            //  ? "-4px 0 4px -4px gray inset"
            //: isFirstRightPinnedColumn
            //? "4px 0 4px -4px gray inset"
            //: undefined,
            left:
                isPinned === "left"
                    ? `${column.getStart("left")}px`
                    : undefined,
            right:
                isPinned === "right"
                    ? `${column.getAfter("right")}px`
                    : undefined,
            //opacity: isPinned ? 0.95 : 1,
            position: isPinned ? "sticky" : "relative",
            width: column.getSize(),
            zIndex: isPinned ? 1 : 0,
            background: "white",
        };
    };

    useEffect(() => {
        // If no rows are selected, just update the ref and return
        if (Object.keys(rowSelection).length === 0) {
            prevDataRef.current = data;
            return;
        }

        // Get the currently selected row index
        const selectedIndex = parseInt(Object.keys(rowSelection)[0]);
        const selectedItem = prevDataRef.current[selectedIndex];

        // Check if the selected item still exists in the new data by comparing the entire item
        const itemStillExists = data.some(
            (item) => JSON.stringify(item) === JSON.stringify(selectedItem),
        );

        if (!itemStillExists) {
            // If the selected item no longer exists, reset selection
            setRowSelection({});
        }

        prevDataRef.current = data;
    }, [data]);

    useEffect(() => {
        table.getColumn("select")?.pin("left");
    }, []);

    useEffect(() => {
        // Return if no rows are selected
        // We commented this out because unselect needs to be propagated to parent
        //if (Object.keys(rowSelection).length === 0) return;

        // Return if there's no handler to process the selection
        if (onRowSelection === undefined) return;

        onRowSelection(rowSelection);
    }, [rowSelection]);

    return (
        <div className="card card-compact w-full h-full flex flex-col bg-white overflow-y-auto">
            <div className="card-title mb-0! sticky top-0 bg-white text-sm pt-4 px-4 pb-1.5">
                {title}
            </div>
            <div
                className={`card-body py-0! px-0! gap-0! block! overflow-hidden h-full`}
            >
                <div className="h-full flex flex-col">
                    <div className="flex-1 overflow-auto">
                        <table
                            className={clsx(
                                "w-full table table-fixed border-collapse border-spacing-0 table-pin-rows",
                                {
                                    "table-xs": tableSize === "xs",
                                    "table-sm": tableSize === "sm",
                                    "table-md": tableSize === "md",
                                    "table-lg": tableSize === "lg",
                                },
                            )}
                        >
                            <thead>
                                {table.getHeaderGroups().map((headerGroup) => {
                                    return (
                                        <tr
                                            key={headerGroup.id}
                                            className="z-2"
                                        >
                                            {headerGroup.headers.map(
                                                (header) => (
                                                    <th
                                                        key={header.id}
                                                        colSpan={header.colSpan}
                                                        className={clsx(
                                                            "align-top",
                                                            {
                                                                xs: "text-xs",
                                                                sm: "text-sm",
                                                                md: "text-base",
                                                                lg: "text-lg",
                                                                default:
                                                                    "text-base",
                                                            }[
                                                                tableSize ??
                                                                    "default"
                                                            ],
                                                        )}
                                                        style={{
                                                            ...getCommonPinningStyles(
                                                                header.column,
                                                            ),
                                                        }}
                                                    >
                                                        <div
                                                            className={clsx(
                                                                "flex items-center",
                                                                {
                                                                    "cursor-pointer":
                                                                        header.column.getCanSort(),
                                                                },
                                                            )}
                                                            onClick={
                                                                enableSorting
                                                                    ? header.column.getToggleSortingHandler()
                                                                    : undefined
                                                            }
                                                        >
                                                            {flexRender(
                                                                header.column
                                                                    .columnDef
                                                                    .header,
                                                                header.getContext(),
                                                            )}

                                                            {enableSorting &&
                                                                {
                                                                    asc: (
                                                                        <TiArrowSortedUp />
                                                                    ),
                                                                    desc: (
                                                                        <TiArrowSortedUp className="rotate-180" />
                                                                    ),
                                                                }[
                                                                    header.column.getIsSorted() as string
                                                                ]}
                                                        </div>
                                                        {header.column.getCanFilter() ? (
                                                            <div>
                                                                <Filter
                                                                    column={
                                                                        header.column
                                                                    }
                                                                />
                                                            </div>
                                                        ) : null}
                                                        {/*
                                                        {!header.isPlaceholder &&
                                                            header.column.getCanPin() && (
                                                                <div className="flex gap-1 justify-center">
                                                                    {header.column.getIsPinned() !==
                                                                    "left" ? (
                                                                        <button
                                                                            className="border rounded-sm px-2"
                                                                            onClick={() => {
                                                                                header.column.pin(
                                                                                    "left",
                                                                                );
                                                                            }}
                                                                        >
                                                                            {
                                                                                "<="
                                                                            }
                                                                        </button>
                                                                    ) : null}
                                                                    {header.column.getIsPinned() ? (
                                                                        <button
                                                                            className="border rounded-sm px-2"
                                                                            onClick={() => {
                                                                                header.column.pin(
                                                                                    false,
                                                                                );
                                                                            }}
                                                                        >
                                                                            X
                                                                        </button>
                                                                    ) : null}
                                                                    {header.column.getIsPinned() !==
                                                                    "right" ? (
                                                                        <button
                                                                            className="border rounded-sm px-2"
                                                                            onClick={() => {
                                                                                header.column.pin(
                                                                                    "right",
                                                                                );
                                                                            }}
                                                                        >
                                                                            {
                                                                                "=>"
                                                                            }
                                                                        </button>
                                                                    ) : null}
                                                                </div>
                                                            )}
                                                            */}
                                                    </th>
                                                ),
                                            )}
                                        </tr>
                                    );
                                })}
                            </thead>
                            <tbody>
                                {!orderNotesLoading ? (
                                    table.getRowModel().rows.map((row) => {
                                        return (
                                            <tr key={row.id}>
                                                {row
                                                    .getVisibleCells()
                                                    .map((cell) => (
                                                        <td
                                                            key={cell.id}
                                                            className={clsx({
                                                                "bg-gray-200/70!":
                                                                    row.getIsSelected(),
                                                            })}
                                                            style={{
                                                                ...getCommonPinningStyles(
                                                                    cell.column,
                                                                ),
                                                            }}
                                                        >
                                                            {flexRender(
                                                                cell.column
                                                                    .columnDef
                                                                    .cell,
                                                                cell.getContext(),
                                                            )}
                                                        </td>
                                                    ))}
                                            </tr>
                                        );
                                    })
                                ) : (
                                    <div className="w-full h-full flex justify-center items-center flex-row">
                                        <span className="loading loading-spinner loading-lg text-white"></span>
                                    </div>
                                )}
                            </tbody>
                        </table>
                    </div>

                    {enablePagination && (
                        <div className="flex justify-between items-center p-4 border-t">
                            <button
                                className="btn btn-sm"
                                onClick={() => table.previousPage()}
                                disabled={!table.getCanPreviousPage()}
                            >
                                <FaChevronLeft />
                            </button>

                            <span>
                                Page:{" "}
                                {table.getPageCount() > 0
                                    ? `${
                                          table.getState().pagination
                                              .pageIndex + 1
                                      } / ${table.getPageCount()}`
                                    : "0 / 0"}
                            </span>

                            <button
                                className="btn btn-sm"
                                onClick={() => table.nextPage()}
                                disabled={!table.getCanNextPage()}
                            >
                                <FaChevronRight />
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

function Filter({ column }: { column: Column<any, unknown> }) {
    // @ts-ignore
    const { filterVariant } = column.columnDef.meta ?? {};

    const columnFilterValue = column.getFilterValue();

    const sortedUniqueValues = useMemo(
        () =>
            filterVariant === "range"
                ? []
                : Array.from(column.getFacetedUniqueValues().keys())
                      .sort()
                      .slice(0, 5000),
        [column.getFacetedUniqueValues(), filterVariant],
    );

    return filterVariant === "range" ? (
        <div>
            <div className="flex space-x-2">
                <DebouncedInput
                    type="number"
                    min={Number(column.getFacetedMinMaxValues()?.[0] ?? "")}
                    max={Number(column.getFacetedMinMaxValues()?.[1] ?? "")}
                    value={(columnFilterValue as [number, number])?.[0] ?? ""}
                    onChange={(value) =>
                        column.setFilterValue((old: [number, number]) => [
                            value,
                            old?.[1],
                        ])
                    }
                    placeholder={`Min ${
                        column.getFacetedMinMaxValues()?.[0] !== undefined
                            ? `(${column.getFacetedMinMaxValues()?.[0]})`
                            : ""
                    }`}
                    className="border shadow-sm rounded-sm"
                />
                <DebouncedInput
                    type="number"
                    min={Number(column.getFacetedMinMaxValues()?.[0] ?? "")}
                    max={Number(column.getFacetedMinMaxValues()?.[1] ?? "")}
                    value={(columnFilterValue as [number, number])?.[1] ?? ""}
                    onChange={(value) =>
                        column.setFilterValue((old: [number, number]) => [
                            old?.[0],
                            value,
                        ])
                    }
                    placeholder={`Max ${
                        column.getFacetedMinMaxValues()?.[1]
                            ? `(${column.getFacetedMinMaxValues()?.[1]})`
                            : ""
                    }`}
                    className="border shadow-sm rounded-sm"
                />
            </div>
            <div className="h-1" />
        </div>
    ) : filterVariant === "select" ? (
        <select
            onChange={(e) => column.setFilterValue(e.target.value)}
            value={columnFilterValue?.toString()}
            className="select select-xs w-full"
        >
            <option value="">All</option>
            {sortedUniqueValues.map((value) => (
                //dynamically generated select options from faceted values feature
                <option value={value} key={value}>
                    {value}
                </option>
            ))}
        </select>
    ) : (
        <>
            {/* Autocomplete suggestions from faceted values feature */}
            <datalist id={column.id + "list"}>
                {sortedUniqueValues.map((value: any) => (
                    <option value={value} key={value} />
                ))}
            </datalist>
            <DebouncedInput
                type="text"
                value={(columnFilterValue ?? "") as string}
                onChange={(value) => column.setFilterValue(value)}
                placeholder={`Search... (${
                    column.getFacetedUniqueValues().size
                })`}
                className="input input-xs w-full"
                list={column.id + "list"}
            />
            {/*<div className="h-1" />*/}
        </>
    );
}

function IndeterminateCheckbox({
    indeterminate,
    className = "",
    ...rest
}: { indeterminate?: boolean } & HTMLProps<HTMLInputElement>) {
    const ref = useRef<HTMLInputElement>(null!);

    useEffect(() => {
        if (typeof indeterminate === "boolean") {
            ref.current.indeterminate = !rest.checked && indeterminate;
        }
    }, [ref, indeterminate]);

    return (
        <input
            type="checkbox"
            ref={ref}
            className={className + " checkbox checkbox-xs"}
            {...rest}
        />
    );
}
